import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projects } from '../data';
import { NeonText } from '../components/effects';
import { Button, Carousel, InstagramEmbed, QuotesCarousel } from '../components/common';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { RetroGrid } from '../components/effects';
import { useLanguage } from '../i18n';
import styles from './ProjectPage.module.css';

// Parse markdown bold (**text**) to JSX
function parseMarkdownBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projects.find((p) => p.id === projectId);
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectId]);

  if (!project) {
    return <Navigate to="/proyectos" replace />;
  }

  // Get localized description
  const fullDescription = language === 'en' && project.fullDescriptionEn
    ? project.fullDescriptionEn
    : project.fullDescription;

  const description = language === 'en' && project.descriptionEn
    ? project.descriptionEn
    : project.description;

  return (
    <>
      <RetroGrid />
      <Header />

      <main className={styles.main}>
        <div className={styles.container}>
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link to="/proyectos" className={styles.backLink}>
              {t.projectPage.backToProjects}
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            className={styles.header}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className={styles.headerTop}>
              {(project.image || (project.images && project.images.length > 0)) && (
                <div className={styles.headerImage}>
                  <img
                    src={project.image || project.images![0]}
                    alt={project.title}
                  />
                </div>
              )}

              <NeonText as="h1" color="pink" effect="glow" className={styles.title} animate={false}>
                {project.title}
              </NeonText>
            </div>

            <div className={styles.meta}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.projectPage.platform}</span>
                <span className={styles.metaValue}>{project.platform}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.projectPage.technology}</span>
                <span className={styles.metaValue}>{project.technology.join(', ')}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.projectPage.client}</span>
                <span className={styles.metaValue}>{project.client}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.projectPage.date}</span>
                <span className={styles.metaValue}>{project.date}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.projectPage.location}</span>
                <span className={styles.metaValue}>{project.location}</span>
              </div>
            </div>
          </motion.header>

          {/* Carousel Section */}
          <motion.section
            className={styles.carouselSection}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {project.id === 'arcadia-earth-ar-toronto' ? (
              <InstagramEmbed posts={project.instagramPosts!} />
            ) : (
              <Carousel
                images={project.images}
                videoFile={project.videoFile}
                videoUrl={project.videoUrl}
                alt={project.title}
              />
            )}
          </motion.section>

          {/* Content */}
          <motion.section
            className={styles.content}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <span className={styles.sectionLabel}>{t.projectPage.project}</span>
            <h2 className={styles.sectionTitle}>{description}</h2>

            <div className={styles.description}>
              {fullDescription.length > 0 && (
                <p>{parseMarkdownBold(fullDescription[0])}</p>
              )}
              {fullDescription.length > 1 && (
                <ul className={styles.descriptionList}>
                  {fullDescription.slice(1).map((item: string, index: number) => (
                    <li key={index}>- {parseMarkdownBold(item)}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* External Link */}
            {project.externalUrl && (
              <div className={styles.externalLink}>
                <Button
                  href={project.externalUrl}
                  variant="outline"
                  size="lg"
                >
                  {project.externalUrlLabel || t.projectPage.viewExternalProject} →
                </Button>
              </div>
            )}

          </motion.section>

          {/* Quotes */}
          {project.quotes && project.quotes.length > 0 && (
            <motion.section
              className={styles.quotesSection}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
            >
              <QuotesCarousel quotes={project.quotes} autoPlayInterval={6000} />
            </motion.section>
          )}

          {/* Tech Stack */}
          <motion.section
            className={styles.techSection}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3 className={styles.techTitle}>{t.projectPage.techUsed}</h3>
            <div className={styles.techStack}>
              {project.technology.map((tech) => (
                <span key={tech} className={styles.techTag}>
                  {tech}
                </span>
              ))}
            </div>
          </motion.section>

          {/* CTA */}
          <motion.div
            className={styles.cta}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <p>{t.projectPage.projectInMind}</p>
            <p className={styles.ctaSubtext}>
              {t.projectPage.projectInMindSubtext}
            </p>
            <Button href="/#contact" variant="primary" size="lg">
              {t.hero.contactMe}
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
